import { ReactNode, useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router';
import { useAuthStatus } from '../auth/useAuthStatus';
import ConfiguringLoader from './ConfiguringLoader';

interface ProtectedRouteProps {
    children: ReactNode;
}

function ProtectedRoute({ children }: ProtectedRouteProps) {
    // Unified across Cognito (admins/SSO) AND the self-issued signer session
    // (email-only signers, #2955).
    const authStatus = useAuthStatus();
    const location = useLocation();
    // Tracked as state set from an effect, not a ref written/read during render
    // (react-hooks/refs, #3347) — a render can be discarded or double-invoked
    // without committing, so a ref mutated during render isn't guaranteed to
    // reflect the committed value. By the time authStatus transitions away from
    // 'authenticated', this effect has already committed `true` from the prior
    // render where it was 'authenticated'.
    const [wasAuthenticated, setWasAuthenticated] = useState(false);

    useEffect(() => {
        if (authStatus === 'authenticated') {
            setWasAuthenticated(true);
        }
    }, [authStatus]);

    if (authStatus === 'configuring') {
        // Shared bounded loader (issue #2595): spinner first, then a manual
        // "Sign in" escape hatch if configuring persists. This is the COMMON
        // stuck-spinner site — an idle user on /dashboard lands here.
        return <ConfiguringLoader />;
    }

    if (authStatus !== 'authenticated') {
        // Only mark as session-expired if the user was previously authenticated in this session
        return <Navigate to="/login" state={{ from: location, sessionExpired: wasAuthenticated }} replace />;
    }

    return <>{children}</>;
}

export default ProtectedRoute;
